import "./style.scss";

import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col } from "@amzn/stencil-react-components/layout";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { ModalContent } from "@amzn/stencil-react-components/modal";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Label, Text } from "@amzn/stencil-react-components/text";
import Button from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import Link from "@amzn/uxm-stencil-react-components/lib/src/stencil/link";
import TextArea from "@amzn/uxm-stencil-react-components/lib/src/stencil/text-area";
import { ApprovalsListForModal } from "common/components/Approvals/ApprovalsListForModal";
import { InlineUserReference } from "common/components/InlineUserReference";
import { License } from "common/components/LicenseSelect/LicenseSelect";
import { CreateOrderRequestContent } from "common/types";
import { PreCheckoutInformation } from "common/types";
import { MessageStruct } from "common/types/ui-types";
import { getString } from "common/uistringlabels/uiStringUtils";
import { UserPermissions } from "lib/services/user-permissions";
import _ from "lodash";
import React, { FunctionComponent, useEffect, useState } from "react";

import { AuditTemp } from "../..";
import { AcknowledgmentInfo, AcknowledgmentsList } from "../Acknowledgments";
import { MessagesList } from "../MessagesList";

const acknowledgePolicyName = getString("submitSanctionOrder.policy.name");
const ACK_USE_POLICY: AcknowledgmentInfo = {
  id: "checkReadAcceptableUsePolicy",
  input: acknowledgePolicyName,
  policies: [acknowledgePolicyName],
  dataCy: "read-acceptable-use-policy-checkbox",
  text: (
    <Text>
      {getString("submitSanctionOrder.acceptConditions.part1")}
      <Link href={getString("submitSanctionOrder.policy.url")} touchpoint="acceptable-use-policy-link" target="_blank">
        {getString("submitSanctionOrder.policy.name")}
      </Link>
      {getString("submitSanctionOrder.acceptConditions.part2")}
    </Text>
  ),
};

export interface SubmitSanctionedOrderModalProps {
  name: string;
  taxonomyId: string;
  submitOrderHandler: (request: CreateOrderRequestContent) => void;
  preCheckoutInformation?: PreCheckoutInformation;
  close: () => void;
  csrfTokenIsLoading: boolean;
  isLoadingCreate: boolean;
  messages?: MessageStruct[];
  externalButtonText?: string;
  orderWorkflow: string;
  licenseInfo?: License;
  isOsCompatible?: boolean;
}

/**
 * Reusable component to submit a sanctioned order
 */

export const SubmitSanctionedOrderModal: FunctionComponent<SubmitSanctionedOrderModalProps> = ({
  name,
  taxonomyId,
  preCheckoutInformation,
  submitOrderHandler,
  close,
  csrfTokenIsLoading,
  isLoadingCreate,
  messages,
  externalButtonText,
  orderWorkflow,
  licenseInfo,
  isOsCompatible,
}: SubmitSanctionedOrderModalProps) => {
  const userAlias = UserPermissions.current.username;
  const [confirmIsChecked, setConfirmIsChecked] = useState(false);
  const [comment, setComment] = useState<undefined | string>(undefined);
  const [acknowledgementAudit, setAcknowledgementAudit] = useState<AuditTemp>({});
  const [acksList, setAckList] = useState<AcknowledgmentInfo[] | undefined>();

  const submitOrder = async () => {
    const request: CreateOrderRequestContent = {
      items: {
        [taxonomyId!]: {
          quantity: 1,
          ...(licenseInfo !== undefined && {
            details: {
              licenseInfo,
            },
          }),
        },
      },
      comment,
      acknowledgementAudit: _.values(acknowledgementAudit),
    };
    await submitOrderHandler(request);
  };

  /* istanbul ignore next */
  const canClickSubmit = () => {
    if (!confirmIsChecked || isLoadingCreate) return false;
    if (preCheckoutInformation !== undefined && preCheckoutInformation!.canBeAutoApproved) return true;
    return comment != undefined && comment.trim();
  };

  const handleConfirmCheck = (policies: string[], ack: boolean, input?: string) => {
    for (let i = 0; i < policies.length; i++) {
      const policyName = policies[i];
      acknowledgementAudit[policyName] = {
        by: userAlias!,
        on: new Date(),
        policyName: policyName,
        acknowledged: ack,
      };
    }
  };

  const nonCompatibleOsDisclaimerQuery = (
    <Col gridGap="S200" alignSelf="flex-start" width={"100%"}>
      <MessageBanner type={MessageBannerType.Warning} dataTestId="os-disclaimer-query">
        {getString("nonCompatibleOS.disclaimerInformation")}
      </MessageBanner>
    </Col>
  );

  /* istanbul ignore next */
  const selectModalBodyComponent = () => {
    if (!preCheckoutInformation || csrfTokenIsLoading) {
      return <Spinner size={SpinnerSize.Medium} fr={undefined} />;
    }

    if (preCheckoutInformation.canBeAutoApproved) {
      return (
        <Text color="neutral90" fontSize="T300">
          {getString("submitSanctionOrder.autoApproveOrderBody")}
        </Text>
      );
    }

    const requiredApprover = preCheckoutInformation.requiredSTPApprover;

    return (
      <Col data-testid="submit-sanctioned-modal" gridGap="S100">
        <ApprovalsListForModal requiredApprovals={preCheckoutInformation.requiredApprovals}></ApprovalsListForModal>

        {requiredApprover && (
          <Text color="neutral90" fontSize="T200">
            {getString("submitSanctionOrder.managerRequest.body")}
            <strong>
              {" "}
              <InlineUserReference userReference={requiredApprover}></InlineUserReference>{" "}
            </strong>
          </Text>
        )}

        <Label htmlFor="comment-text-area">{getString("submitSanctionOrder.managerRequest.textAreaLabel")}</Label>
        <TextArea
          id="comment-text-area"
          data-cy="comment-text-area"
          touchpoint="sanctioned-comment-text-area"
          placeholder={getString("submitSanctionOrder.managerRequest.textAreaPlaceHolder")}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <br />
      </Col>
    );
  };

  const modalBodyComponent = selectModalBodyComponent();

  // Builds the ACK list
  useEffect(() => {
    const list: AcknowledgmentInfo[] = [];
    if (preCheckoutInformation?.acksList) {
      preCheckoutInformation.acksList.forEach((i) => list!.push(i));

      // Default policy checkbox
      list.push(ACK_USE_POLICY);
    }
    setAckList(list);
  }, [preCheckoutInformation?.acksList]);

  return (
    <ModalContent
      titleText={name ? name : ""}
      buttons={[
        <Button
          key="submitOrder-close"
          touchpoint="sanctioned-cancel-button"
          variant={ButtonVariant.Tertiary}
          onClick={close}
        >
          Cancel
        </Button>,
        <Button
          key="submitOrder-submit"
          data-testid="submitOrder-submit"
          data-cy="submit-order-button"
          variant={ButtonVariant.Primary}
          disabled={!canClickSubmit()}
          touchpoint="sanctioned-submit-button"
          onClick={submitOrder}
        >
          {orderWorkflow.startsWith("external") && externalButtonText && preCheckoutInformation?.canBeAutoApproved
            ? externalButtonText
            : "Submit"}
          {isLoadingCreate ? <Spinner size={SpinnerSize.Small} fr={undefined} /> : null}
        </Button>,
      ]}
    >
      <Col>
        <MessagesList messages={messages}></MessagesList>
      </Col>
      <Col gridGap="S200" alignSelf="flex-start">
        {modalBodyComponent}
      </Col>
      {!isOsCompatible && (
        <Col gridGap="S400" alignItems="start" width={"100%"}>
          {nonCompatibleOsDisclaimerQuery}
        </Col>
      )}
      <Col gridGap="S400" rowGap="S200" alignItems="center">
        <AcknowledgmentsList list={acksList} handleSingleCheck={handleConfirmCheck} handleReady={setConfirmIsChecked} />
        <Col gridGap="S200" alignSelf="flex-start">
          <Text fontSize="T100" color="neutral70">
            {getString("submitSanctionOrder.requiredField")}
          </Text>
        </Col>
      </Col>
    </ModalContent>
  );
};
